import { mockData } from '../mocks/mockData';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  accountId?: string;
}

// Função para registrar status da rota (será injetada pelo contexto)
let routeMonitor: ((route: string, status: any) => void) | null = null;

export const setRouteMonitor = (monitor: (route: string, status: any) => void) => {
  routeMonitor = monitor;
};

const logRouteStatus = (route: string, status: any) => {
  if (routeMonitor) {
    routeMonitor(route, status);
  }
};

class ApiService {
  private async refreshAccessToken(): Promise<string | null> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('auth_token', data.access_token);
        return data.access_token;
      } else {
        // Refresh token expirado, fazer logout
        this.logout();
        return null;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      this.logout();
      return null;
    }
  }

  private async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    let token = localStorage.getItem('auth_token');
    
    // Se não há token, tentar refresh
    if (!token) {
      token = await this.refreshAccessToken();
    }

    if (!token) {
      throw new Error('No valid authentication token');
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
        ...options,
      });

      // Se token expirou, tentar refresh e repetir
      if (response.status === 401) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${newToken}`,
              ...options.headers,
            },
            ...options,
          });

          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }

          const data = await retryResponse.json();
          return { data, success: true };
        } else {
          throw new Error('Authentication failed');
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data, success: true };
    } catch (error) {
      console.warn(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data, success: true };
    } catch (error) {
      console.warn(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<{ user: User; tokens: AuthTokens; enrichedData?: any }> {
    const response = await this.makeRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Backend retorna diretamente os dados, não em response.data
    if ((response as any).access_token && (response as any).user) {
      const { access_token, refresh_token, user, enrichedData } = response as any;

      return {
        user,
        tokens: { access_token, refresh_token },
        enrichedData
      };
    }

    throw new Error('Login failed');
  }

  async refreshToken(refreshToken: string): Promise<{ access_token: string; refresh_token: string }> {
    const response = await this.makeRequest('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if ((response as any).access_token) {
      return {
        access_token: (response as any).access_token,
        refresh_token: (response as any).refresh_token || refreshToken
      };
    }

    throw new Error('Token refresh failed');
  }

  async register(userData: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    role?: string;
  }): Promise<{ user: User; tokens: AuthTokens }> {
    const response = await this.makeRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.success && response.data) {
      const { access_token, refresh_token, user } = response.data as any;
      
      // Salvar tokens
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { user, tokens: { access_token, refresh_token } };
    }

    throw new Error('Registration failed');
  }

  async createAccount(accountData: {
    name: string;
    description?: string;
    owner: {
      email: string;
      password: string;
      name: string;
    };
  }): Promise<{ account: any; owner: User; tokens: AuthTokens }> {
    const response = await this.makeRequest('/accounts', {
      method: 'POST',
      body: JSON.stringify(accountData),
    });

    if (response.success && response.data) {
      const { account, owner } = response.data as any;
      
      // Se o registro inclui tokens (para o owner), salvá-los
      if (response.data.access_token && response.data.refresh_token) {
        localStorage.setItem('auth_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(owner));
      }
      
      return { 
        account, 
        owner, 
        tokens: { 
          access_token: response.data.access_token || '', 
          refresh_token: response.data.refresh_token || '' 
        } 
      };
    }

    throw new Error('Account creation failed');
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await this.makeRequest('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      }
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  }

  async logoutAll(): Promise<void> {
    try {
      await this.makeAuthenticatedRequest('/auth/logout-all', {
        method: 'POST',
      });
    } catch (error) {
      console.warn('Logout all request failed:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  }

  async getProfile(): Promise<User> {
    const response = await this.makeAuthenticatedRequest('/auth/profile');
    return response.data;
  }

  async updateProfile(profileData: {
    name?: string;
    email?: string;
    avatar?: string;
  }): Promise<User> {
    const response = await this.makeAuthenticatedRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return response.data;
  }

  async changePassword(passwordData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ message: string }> {
    const response = await this.makeAuthenticatedRequest('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
    return response.data;
  }

  async getActiveSessions(): Promise<any[]> {
    const response = await this.makeAuthenticatedRequest('/auth/sessions');
    return response.data;
  }

  // User management endpoints
  async getUsers(): Promise<User[]> {
    const response = await this.makeAuthenticatedRequest('/api/users');
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`/api/users/${id}`);
    return response.data;
  }

  async createUser(userData: {
    name: string;
    email: string;
    password: string;
    role: string;
    accountId?: string;
    avatar?: string;
  }): Promise<User> {
    const response = await this.makeAuthenticatedRequest('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return response.data;
  }

  async updateUser(id: string, userData: {
    name?: string;
    email?: string;
    role?: string;
    avatar?: string;
    isActive?: boolean;
  }): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.makeAuthenticatedRequest(`/api/users/${id}`, {
      method: 'DELETE',
    });
  }

  async deactivateUser(id: string): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`/api/users/${id}/deactivate`, {
      method: 'PUT',
    });
    return response.data;
  }

  async activateUser(id: string): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`/api/users/${id}/activate`, {
      method: 'PUT',
    });
    return response.data;
  }

  // Account management endpoints
  async getAllAccounts(): Promise<any[]> {
    const response = await this.makeAuthenticatedRequest('/api/accounts');
    return response.data;
  }

  async getAccount(id: string): Promise<any> {
    const response = await this.makeAuthenticatedRequest(`/api/accounts/${id}`);
    return response.data;
  }

  async updateAccount(id: string, accountData: {
    name?: string;
    description?: string;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.makeAuthenticatedRequest(`/api/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(accountData),
    });
    return response.data;
  }

  async deleteAccount(id: string): Promise<void> {
    await this.makeAuthenticatedRequest(`/api/accounts/${id}`, {
      method: 'DELETE',
    });
  }

  async deactivateAccount(id: string): Promise<any> {
    const response = await this.makeAuthenticatedRequest(`/api/accounts/${id}/deactivate`, {
      method: 'PUT',
    });
    return response.data;
  }

  async activateAccount(id: string): Promise<any> {
    const response = await this.makeAuthenticatedRequest(`/api/accounts/${id}/activate`, {
      method: 'PUT',
    });
    return response.data;
  }

  async getAccountUsers(id: string): Promise<User[]> {
    const response = await this.makeAuthenticatedRequest(`/api/accounts/${id}/users`);
    return response.data;
  }

  async getAccountStats(id: string): Promise<any> {
    const response = await this.makeAuthenticatedRequest(`/api/accounts/${id}/stats`);
    return response.data;
  }

  // Roles endpoints
  async getAllRoles(): Promise<any[]> {
    const response = await this.makeAuthenticatedRequest('/api/roles');
    return response.data;
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/test`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

export const apiService = new ApiService();

// Service with fallback to mock data - VALIDAÇÃO POR REQUISIÇÃO
export class ApiServiceWithFallback {
  private async tryApi<T>(apiCall: () => Promise<T>): Promise<T> {
    try {
      return await apiCall();
    } catch (error) {
      // Log apenas em produção ou quando não for erro de API indisponível
      if (!import.meta.env.DEV || !(error instanceof Error) || !error.message.includes('fetch')) {
        console.warn('API call failed, using mock data:', error);
      }
      throw new Error('API_UNAVAILABLE');
    }
  }

  // HTTP methods with fallback - VALIDAÇÃO INDIVIDUAL
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    const startTime = Date.now();
    console.log(`🔍 ApiServiceWithFallback.get: Testando endpoint ${endpoint}`);
    
    try {
      const result = await this.tryApi(() => apiService.makeRequest<T>(endpoint, { method: 'GET' }));
      const responseTime = Date.now() - startTime;
      
      console.log(`✅ ApiServiceWithFallback.get: API real funcionou para ${endpoint}`, result);
      
      logRouteStatus(endpoint, {
        dataSource: 'api',
        success: true,
        responseTime,
      });
      
      return result;
    } catch (error) {
      const responseTime = Date.now() - startTime;
      console.log(`⚠️ ApiServiceWithFallback.get: API falhou para ${endpoint}, usando fallback`, error);
      
      // Fallback to mock data based on endpoint
      if (endpoint === '/api/users') {
        console.log(`🎭 ApiServiceWithFallback.get: Usando mock data para ${endpoint}`);
        const mockUsers = mockData.getAllUsers();
        console.log(`🎭 ApiServiceWithFallback.get: Mock users encontrados:`, mockUsers.length);
        
        logRouteStatus(endpoint, {
          dataSource: 'mock',
          success: true,
          responseTime,
          error: 'API_UNAVAILABLE',
        });
        
        return {
          data: mockUsers as T,
          success: true
        };
      }

      if (endpoint === '/api/roles') {
        console.log(`🎭 ApiServiceWithFallback.get: Usando mock data para ${endpoint}`);
        const mockRoles = mockData.getAllRoles();
        console.log(`🎭 ApiServiceWithFallback.get: Mock roles encontrados:`, mockRoles.length);
        
        logRouteStatus(endpoint, {
          dataSource: 'mock',
          success: true,
          responseTime,
          error: 'API_UNAVAILABLE',
        });
        
        return {
          data: mockRoles as T,
          success: true
        };
      }
      
      if (endpoint.startsWith('/api/accounts/') && endpoint.endsWith('/users')) {
        const accountId = endpoint.split('/')[3];
        console.log(`🎭 ApiServiceWithFallback.get: Usando mock data para usuários da conta ${accountId}`);
        
        logRouteStatus(endpoint, {
          dataSource: 'mock',
          success: true,
          responseTime,
          error: 'API_UNAVAILABLE',
        });
        
        return {
          data: mockData.getUsersByAccount(accountId) as T,
          success: true
        };
      }
      
      if (endpoint.startsWith('/api/users/')) {
        const userId = endpoint.split('/')[3];
        const allUsers = mockData.getAllUsers();
        const user = allUsers.find(u => u.id === userId);
        if (user) {
          console.log(`🎭 ApiServiceWithFallback.get: Usando mock data para usuário ${userId}`);
          
          logRouteStatus(endpoint, {
            dataSource: 'mock',
            success: true,
            responseTime,
            error: 'API_UNAVAILABLE',
          });
          
          return {
            data: user as T,
            success: true
          };
        }
      }
      
      console.log(`❌ ApiServiceWithFallback.get: Endpoint ${endpoint} não encontrado no fallback`);
      
      logRouteStatus(endpoint, {
        dataSource: 'unknown',
        success: false,
        responseTime,
        error: 'Endpoint not found in fallback',
      });
      
      throw new Error('Endpoint not found in fallback');
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      return await this.tryApi(() => apiService.makeRequest<T>(endpoint, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' }
      }));
    } catch (error) {
      throw new Error('POST not implemented in fallback');
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      return await this.tryApi(() => apiService.makeRequest<T>(endpoint, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' }
      }));
    } catch (error) {
      throw new Error('PUT not implemented in fallback');
    }
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      return await this.tryApi(() => apiService.makeRequest<T>(endpoint, { method: 'DELETE' }));
    } catch (error) {
      throw new Error('DELETE not implemented in fallback');
    }
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      return await this.tryApi(() => apiService.makeRequest<T>(endpoint, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' }
      }));
    } catch (error) {
      throw new Error('PATCH not implemented in fallback');
    }
  }

  // Auth methods with fallback - VALIDAÇÃO INDIVIDUAL
  async login(email: string, password: string) {
    try {
      return await this.tryApi(() => apiService.login(email, password));
    } catch (error) {
      // Fallback to mock authentication - usando a nova estrutura multi-tenant
      const allUsers = mockData.getAllUsers();
      const mockUser = allUsers.find(
        user => user.email === email && user.password === password
      );

      if (mockUser) {
        const token = `mock-token-${Date.now()}`;
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(mockUser));

        return {
          data: {
            access_token: token,
            user: {
              id: mockUser.id,
              name: mockUser.name,
              email: mockUser.email,
              role: mockUser.role,
              avatar: mockUser.avatar,
              accountId: mockUser.accountId,
              isActive: mockUser.isActive,
              createdAt: mockUser.createdAt,
            }
          },
          success: true
        };
      }

      throw new Error('Invalid credentials');
    }
  }

  async register(userData: any) {
    try {
      return await this.tryApi(() => apiService.register(userData));
    } catch (error) {
      // Fallback to mock registration - adicionando à conta master por padrão
      const newUser = {
        id: `new-user-${Date.now()}`,
        name: userData.name,
        email: userData.email,
        password: userData.password,
        role: userData.role || 'user',
        avatar: `https://picsum.photos/200/200?random=${Date.now()}`,
        createdAt: new Date().toISOString(),
        isActive: true,
        accountId: mockData.masterAccount.id, // Adiciona à conta master
      };

      // Adiciona o novo usuário ao array apropriado baseado no role
      if (userData.role === 'superadmin') {
        mockData.masterUsers.push(newUser);
      } else {
        mockData.users.push(newUser);
      }

      const token = `mock-token-${Date.now()}`;
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_user', JSON.stringify(newUser));

      return {
        data: {
          access_token: token,
          user: {
            id: newUser.id,
            name: newUser.name,
            email: newUser.email,
            role: newUser.role,
            avatar: newUser.avatar,
            accountId: newUser.accountId,
            isActive: newUser.isActive,
            createdAt: newUser.createdAt,
          }
        },
        success: true
      };
    }
  }

  async getUsers() {
    try {
      return await this.tryApi(() => apiService.getUsers());
    } catch (error) {
      // Fallback to mock users - usando a nova estrutura multi-tenant
      const allUsers = mockData.getAllUsers();
      return {
        data: allUsers.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          avatar: user.avatar,
          isActive: user.isActive,
          createdAt: user.createdAt,
          updatedAt: user.createdAt,
          accountId: user.accountId,
        })),
        success: true
      };
    }
  }

  // Accounts methods with fallback - VALIDAÇÃO INDIVIDUAL
  async createAccount(data: any) {
    try {
      return await this.tryApi(() => apiService.createAccount(data));
    } catch (error) {
      // Fallback to mock account creation
      const newAccount = {
        id: `550e8400-e29b-41d4-a716-${Date.now()}`,
        name: data.name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const newOwner = {
        id: `550e8400-e29b-41d4-a716-${Date.now() + 1}`,
        name: data.owner.name,
        email: data.owner.email,
        password: data.owner.password,
        role: 'admin',
        avatar: `https://picsum.photos/200/200?random=${Math.floor(Math.random() * 100)}`,
        isActive: true,
        createdAt: new Date().toISOString(),
        accountId: newAccount.id,
      };

      // Adicionar à estrutura de dados mock
      mockData.accounts.push(newAccount);
      mockData.clientUsers.push(newOwner);

      return {
        data: {
          account: newAccount,
          owner: newOwner,
        },
        success: true
      };
    }
  }

  async getAllAccounts() {
    try {
      return await this.tryApi(() => apiService.getAllAccounts());
    } catch (error) {
      // Fallback to mock accounts
      return {
        data: mockData.getAccountsWithUsers(),
        success: true
      };
    }
  }

  async getAccount(id: string) {
    try {
      return await this.tryApi(() => apiService.getAccount(id));
    } catch (error) {
      // Fallback to mock account
      const account = mockData.getAccountById(id);
      if (!account) {
        throw new Error('Account not found');
      }
      return {
        data: {
          ...account,
          users: mockData.getUsersByAccount(account.id)
        },
        success: true
      };
    }
  }

  async getAccountUsers(id: string) {
    try {
      return await this.tryApi(() => apiService.getAccountUsers(id));
    } catch (error) {
      // Fallback to mock account users
      return {
        data: mockData.getUsersByAccount(id),
        success: true
      };
    }
  }

  async getAllRoles() {
    try {
      return await this.tryApi(() => apiService.makeRequest('/roles', { method: 'GET' }));
    } catch (error) {
      // Fallback to mock roles
      return {
        data: mockData.getAllRoles(),
        success: true
      };
    }
  }

  async getProfile() {
    try {
      return await this.tryApi(() => apiService.getProfile());
    } catch (error) {
      // Fallback to mock profile
      const userStr = localStorage.getItem('auth_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return {
          data: {
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role,
            avatar: user.avatar,
            isActive: user.isActive || true,
            createdAt: user.createdAt,
            updatedAt: user.createdAt,
            accountId: user.accountId,
          },
          success: true
        };
      }
      throw new Error('User not found');
    }
  }

  async updateProfile(profileData: any) {
    try {
      return await this.tryApi(() => apiService.updateProfile(profileData));
    } catch (error) {
      // Fallback to mock profile update
      const userStr = localStorage.getItem('auth_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const updatedUser = { ...user, ...profileData };
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));

        return {
          data: {
            id: updatedUser.id,
            name: updatedUser.name,
            email: updatedUser.email,
            role: updatedUser.role,
            avatar: updatedUser.avatar,
            isActive: updatedUser.isActive || true,
            createdAt: updatedUser.createdAt,
            updatedAt: new Date().toISOString(),
            accountId: updatedUser.accountId,
          },
          success: true
        };
      }
      throw new Error('User not found');
    }
  }

  async healthCheck(): Promise<boolean> {
    try {
      return await this.tryApi(() => apiService.healthCheck());
    } catch {
      // Se a API não estiver disponível, retorna false para usar mock
      return false;
    }
  }
}

export const apiServiceWithFallback = new ApiServiceWithFallback();